import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { EntityRefLink } from '@backstage/plugin-catalog-react';

import {
  getSurfaceBorder,
  getSurfaceColor,
  getThemeTokens,
  radii,
} from '../../theme/tokens';
import { useEntityRefInfoMap, type EntityInfo } from './useUsersByEmail';

export type { EntityInfo };

export const EntityRefChips = ({
  refs,
  entityMap: entityMapProp,
  emptyText = '—',
}: {
  refs: string[];
  entityMap?: Map<string, EntityInfo>;
  emptyText?: string;
}) => {
  const theme = useTheme();
  const tokens = getThemeTokens(theme);
  // Fetch entity info only when no map is provided. Always call the hook
  // (rules of hooks), but pass empty refs when the map is supplied externally.
  const fetchedMap = useEntityRefInfoMap(entityMapProp ? [] : refs);
  const entityMap = entityMapProp ?? fetchedMap;

  if (!refs.length) {
    return (
      <Typography sx={{ color: tokens.textMuted, fontSize: 14 }}>
        {emptyText}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {refs.map(ref => {
        const entity = entityMap.get(ref);
        return (
          <EntityRefLink
            key={ref}
            entityRef={ref}
            title={entity?.displayName}
            style={{ textDecoration: 'none' }}
          >
            <Chip
              clickable
              avatar={
                <Avatar
                  alt={entity?.displayName}
                  src={entity?.picture}
                  sx={{ width: 24, height: 24 }}
                />
              }
              label={entity?.displayName ?? ref.split('/').pop() ?? ref}
              size="small"
              sx={{
                backgroundColor: getSurfaceColor(theme, 'subtle'),
                border: `1px solid ${getSurfaceBorder(theme)}`,
                borderRadius: `${radii.sm}px`,
                color: tokens.textPrimary,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                height: 32,
                '& [class*="MuiChip-label"]': { pl: 0.625, pr: 1.25 },
                '&:hover': {
                  backgroundColor: getSurfaceColor(theme, 'raised'),
                  borderColor: getSurfaceBorder(theme, 'strong'),
                },
              }}
            />
          </EntityRefLink>
        );
      })}
    </Box>
  );
};
